import { t } from "../../../../../utils/i18n";
import React from "react";
import { Field, FormikHelpers, FormikErrors } from "formik";
import { Option } from "react-select";
import SelectField from "../common/SelectField";
import { titleOptions, genderOptions, dialingCodeOptions } from "./helpers";
import { required } from "../../../../../utils/formValidations";
import occupations from "../data/occupations.json";

interface Props {
  apiErrors?: any;
  errors: FormikErrors<{
    first_name: string;
    last_name: string;
    salutation: string;
    phone_code: string;
    phone_number: string;
    gender: string;
    occupation: string;
    name_on_card: string;
    annual_salary: string;
    account_purpose: string;
    expected_monthly_volume: string;
    terms_accepted: string;
  }>;
  values: {
    first_name: string;
    last_name: string;
  };
  isVirtualCardRequest?: boolean;
  validateForm: () => Partial<FormikHelpers<any>>;
  handleNextStepChange: (
    validateForm: () => Partial<FormikHelpers<any>>,
    step: string
  ) => void;
  setFieldValue: (field: string, value: any) => void;
  handlePreviousStepChange: (step: string) => void;
}

const PersonalDetails: React.FC<Props> = ({
  isVirtualCardRequest,
  apiErrors,
  errors,
  values,
  validateForm,
  setFieldValue,
  handleNextStepChange,
  handlePreviousStepChange,
}) => {
  return (
    <>
      <div className="card-body">
        <div id="personal-details" className="request-details-cont">
          <div className="request-left-col">
            <i className="fas fa-user-circle card-mail-icon"></i>
            <br />
            <h4 className="card-reqform-title font_3">{t("cards.request_form.personal_details")}</h4>
          </div>
          <div className="row card-deck mb-5 request-right-col">
            <form className="col-md-12">
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="salutation" className="card-sub-title">
                    {t("cards.request_form.salutation")}
                  </label>
                  <Field
                    name="salutation"
                    placeholder={t("cards.request_form.select_salutation")}
                    component={SelectField}
                    validate={required}
                    options={titleOptions()}
                    error={errors.salutation}
                  />
                  {errors.salutation && !apiErrors?.salutation_error && (
                    <small className="error">{errors.salutation}</small>
                  )}
                  {apiErrors?.salutation_error && (
                    <small className="error text-danger">
                      {apiErrors.salutation_error}
                    </small>
                  )}
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="first_name" className="card-sub-title">
                    {t("cards.request_form.first_name")}
                  </label>
                  <Field
                    name="first_name"
                    className="form-control"
                    validate={required}
                    error={errors.first_name}
                    onChange={(e) => {
                      setFieldValue("first_name", e.target.value);
                      setFieldValue(
                        "name_on_card",
                        `${e.target.value} ${values.last_name}`
                      );
                    }}
                  />
                  {errors.first_name && !apiErrors?.first_name_error && (
                    <small className="error">{errors.first_name}</small>
                  )}
                  {apiErrors?.first_name_error && (
                    <small className="error text-danger">
                      {apiErrors.first_name_error}
                    </small>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="middle_name" className="card-sub-title">
                    {t("cards.request_form.middle_name")}
                  </label>
                  <Field name="middle_name" className="form-control" />
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="last_name" className="card-sub-title">
                    {t("cards.request_form.last_name")}
                  </label>
                  <Field
                    name="last_name"
                    className="form-control"
                    validate={required}
                    onChange={(e) => {
                      setFieldValue("last_name", e.target.value);
                      setFieldValue(
                        "name_on_card",
                        `${values.first_name} ${e.target.value}`
                      );
                    }}
                  />
                  {errors.last_name && !apiErrors?.last_name_error && (
                    <small className="error">{errors.last_name}</small>
                  )}
                  {apiErrors?.last_name_error && (
                    <small className="error text-danger">
                      {apiErrors.last_name_error}
                    </small>
                  )}
                </div>
              </div>
              <div className="alert alert-warning" role="alert">
                <i
                  className="fa fa-info-circle"
                  style={{ verticalAlign: "middle" }}
                />{" "}
                <span>{t("cards.request_form.enter_your_name_as_it_appears")}</span>
              </div>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="phone_number" className="card-sub-title">
                    {t("cards.request_form.phone_number")}
                  </label>
                  <div className="d-flex">
                    <div
                      style={{
                        width: "50%",
                        marginRight: "7px",
                        fontSize: "14px",
                      }}
                    >
                      <Field
                        name="phone_code"
                        component={SelectField}
                        validate={required}
                        options={dialingCodeOptions}
                        placeholder={t("cards.request_form.code")}
                        error={errors.phone_code}
                      />
                    </div>
                    <Field
                      name="phone_number"
                      type="number"
                      placeholder={t("cards.request_form.enter_phone_number")}
                      className={`form-control ${
                        errors.phone_number ? "is-invalid" : ""
                      }`}
                      validate={required}
                    />
                  </div>
                  {errors.phone_number && !apiErrors?.phone_number_error && (
                    <small className="error">{errors.phone_number}</small>
                  )}
                  {apiErrors?.phone_number_error && (
                    <small className="error text-danger">
                      {apiErrors.phone_number_error}
                    </small>
                  )}
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="gender" className="card-sub-title">
                    {t("cards.request_form.gender")}
                  </label>
                  <Field
                    name="gender"
                    placeholder={t("cards.request_form.select_gender")}
                    component={SelectField}
                    validate={required}
                    options={genderOptions()}
                    error={errors.gender}
                  />
                  {errors.gender && !apiErrors?.gender_error && (
                    <small className="error">{errors.gender}</small>
                  )}
                  {apiErrors?.gender_error && (
                    <small className="error text-danger">
                      {apiErrors.gender_error}
                    </small>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="occupation" className="card-sub-title">
                    {t("cards.request_form.occcupation")}
                  </label>
                  <Field
                    name="occupation"
                    className="form-control"
                    component={SelectField}
                    validate={required}
                    options={occupations}
                    onChange={(option: Option) => {
                      setFieldValue("occupation", option.label);
                    }}
                    error={errors.occupation || apiErrors?.occupation_error}
                  />
                  {errors.occupation && !apiErrors?.occupation && (
                    <small className="error">{errors.occupation}</small>
                  )}
                  {apiErrors?.occupation_error && (
                    <small className="error text-danger">
                      {apiErrors.occupation_error}
                    </small>
                  )}
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="name_on_card" className="card-sub-title">
                    {t("cards.request_form.name_on_card")}
                  </label>
                  <Field
                    name="name_on_card"
                    className="form-control"
                    placeholder={t("cards.request_form.enter_name_on_card")}
                    validate={required}
                  />
                  {errors.name_on_card && !apiErrors?.name_on_card && (
                    <small className="error">{errors.name_on_card}</small>
                  )}
                  {apiErrors?.name_on_card && (
                    <small className="error text-danger">
                      {apiErrors.name_on_card_error}
                    </small>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="annual_salary" className="card-sub-title">
                    {t("cards.request_form.annual_income")}
                  </label>
                  <Field
                    name="annual_salary"
                    className="form-control"
                    placeholder={t("cards.request_form.enter_your_annual_income")}
                    validate={required}
                  />
                  {errors.annual_salary && !apiErrors?.annual_salary && (
                    <small className="error">{errors.annual_salary}</small>
                  )}
                  {apiErrors?.annual_salary_error && (
                    <small className="error text-danger">
                      {apiErrors.annual_salary_error}
                    </small>
                  )}
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="account_purpose" className="card-sub-title">
                    {t("cards.request_form.account_purpose")}
                  </label>
                  <Field
                    name="account_purpose"
                    className="form-control"
                    placeholder={t("cards.request_form.enter_the_account_purpose_e_g")}
                    validate={required}
                  />
                  {errors.account_purpose && !apiErrors?.account_purpose && (
                    <small className="error">{errors.account_purpose}</small>
                  )}
                  {apiErrors?.account_purpose && (
                    <small className="error text-danger">
                      {apiErrors.account_purpose_error}
                    </small>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label
                    htmlFor="expected_monthly_volume"
                    className="card-sub-title"
                  >
                    {t("cards.request_form.expected_monthly_spend")}
                  </label>
                  <Field
                    name="expected_monthly_volume"
                    className="form-control"
                    placeholder={t("cards.request_form.e_g_10_000")}
                    validate={required}
                  />
                  {errors.expected_monthly_volume &&
                    !apiErrors?.expected_monthly_volume && (
                      <small className="error">
                        {errors.expected_monthly_volume}
                      </small>
                    )}
                  {apiErrors?.expected_monthly_volume_error && (
                    <small className="error text-danger">
                      {apiErrors.expected_monthly_volume_error}
                    </small>
                  )}
                </div>
              </div>
              {isVirtualCardRequest && (
                <div className="row">
                  <div className="form-check">
                    <Field
                      type="checkbox"
                      name="terms_accepted"
                      className="form-check-input"
                      placeholder={t("cards.request_form.e_g_10_000")}
                      validate={required}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="terms_accepted"
                    >
                      {t("cards.request_form.i_accept")}{" "}
                      <a href="/virtual-card-terms.pdf" target="_blank">
                        {t("cards.request_form.card_terms_and_user_agreement")}
                      </a>
                    </label>
                    {errors.terms_accepted && !apiErrors?.terms_accepted && (
                      <small className="error" style={{ display: "block" }}>
                        {errors.terms_accepted}
                      </small>
                    )}
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
      <div className="p-3">
        <div className="d-flex flex-row justify-content-between">
          <button
            type="button"
            className="btn btn-outline-purple float-left"
            onClick={(e) => handlePreviousStepChange("cardType")}
          >
            <i className="fa fa-arrow-left"></i> {t("cards.request_form.previous")}
          </button>
          <button
            type="button"
            className="btn btn-purple float-right"
            onClick={(e) =>
              handleNextStepChange(validateForm, "identification")
            }
          >
            {t("cards.request_form.next")} <i className="fa fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </>
  );
};

export default PersonalDetails;
